import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { userPortalApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { 
  Building2, FileText, CreditCard, FolderOpen, Calendar, Euro, Home, LogOut,
  Download, CheckCircle, Clock, AlertCircle, User, MapPin, Phone, Mail,
  CreditCard as Iban, Building, IdCard, ArrowLeft, Eye, ChevronRight,
  FileCheck, UserCircle, Landmark, ChevronDown, FileDown, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import GdprSection from '../../components/GdprSection';
import WelcomeBanner from '../../components/WelcomeBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const UserDashboard = ({ ghostUserId }) => {
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [portalData, setPortalData] = useState(null);
  const [activeTab, setActiveTab] = useState('contratti');
  const [requestingDocs, setRequestingDocs] = useState(false);

  const isGhostMode = !!ghostUserId;

  // Welcome banner: visibile fino a quando l'utente non lo dismette (per-account)
  const welcomeKey = authUser?.id ? `welcome_dismissed_${authUser.id}` : null;
  const [showWelcome, setShowWelcome] = useState(() => {
    if (isGhostMode) return true; // in ghost: mostra sempre il banner così l'admin vede ciò che vede l'utente
    if (!welcomeKey) return false;
    return localStorage.getItem(welcomeKey) !== '1';
  });
  const dismissWelcome = () => {
    if (welcomeKey && !isGhostMode) localStorage.setItem(welcomeKey, '1');
    setShowWelcome(false);
  };

  const fetchPortalData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await userPortalApi.getData(ghostUserId || null);
      setPortalData(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento dati portale');
    } finally {
      setLoading(false);
    }
  }, [ghostUserId]);

  useEffect(() => {
    fetchPortalData();
  }, [fetchPortalData]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const handleBackToAdmin = () => { navigate('/admin/utenti'); };

  const handleDownloadDoc = async (doc) => {
    try {
      const res = await fetch(
        `${API_URL}/contracts/${doc.contract_id}/documenti/${doc.id}/download`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Download fallito');
      const contentType = res.headers.get('content-type') || '';
      let blob;
      if (contentType.includes('application/json')) {
        // Legacy: JSON con file_data base64
        const data = await res.json();
        if (!data.file_data) throw new Error('File non trovato');
        const byteChars = atob(data.file_data);
        const arr = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) arr[i] = byteChars.charCodeAt(i);
        blob = new Blob([arr]);
      } else {
        // Object Storage (S3): blob binario
        blob = await res.blob();
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleRequestDocuments = async () => {
    setRequestingDocs(true);
    try {
      const res = await fetch(`${API_URL}/user/request-documents`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: '' }),
      });
      if (!res.ok) throw new Error('Richiesta fallita');
      toast.success('Richiesta inviata all\'amministratore. Verrai contattato via email.');
    } catch (error) {
      toast.error('Errore durante l\'invio della richiesta');
    } finally {
      setRequestingDocs(false);
    }
  };

  const handleDownloadPersonalDoc = async (doc) => {
    try {
      const userId = doc.user_id || user?.id;
      const res = await fetch(
        `${API_URL}/users/${userId}/documenti/${doc.id}/download`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Download fallito');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleDownloadPropertyDoc = async (doc) => {
    try {
      const res = await fetch(
        `${API_URL}/properties/${doc.property_id}/documenti/${doc.id}/download`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Download fallito');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download');
    }
  };

  const handleDownloadFatturaManuale = async (paymentId) => {
    try {
      const res = await fetch(
        `${API_URL}/payments/${paymentId}/fattura-manuale/download`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Download fallito');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = res.headers.get('content-disposition');
      link.setAttribute('download', disposition?.match(/filename=(.+)/)?.[1] || `fattura_${paymentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Errore durante il download della fattura');
    }
  };

  const getStatoBadge = (stato) => {
    const map = {
      pending: { cls: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In Attesa' },
      open: { cls: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Aperto' },
      completed: { cls: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Pagato' },
      attivo: { cls: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Attivo' },
      cessato: { cls: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Cessato' },
      scaduto: { cls: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'Scaduto' },
      terminato: { cls: 'bg-slate-100 text-slate-700', icon: CheckCircle, label: 'Terminato' },
    };
    return map[stato] || { cls: 'bg-slate-100 text-slate-700', icon: Clock, label: stato || '-' };
  };

  const fmt = (val) => (val || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
  const monthLabel = (m) => {
    const d = new Date(m + (m.length <= 7 ? '-01' : ''));
    return d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!portalData) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <p className="text-slate-500">Nessun dato disponibile</p>
      </div>
    );
  }

  const { user, is_proprietario, is_inquilino, contratti, immobili, pagamenti,
    documenti_personali = [], documenti_immobili = [], documenti_contratto = [], documenti = [],
    documenti_accesso_scaduto = false, grace_months = 6 } = portalData;

  // Use new arrays if available, fallback to legacy 'documenti' for contract docs
  const contractDocs = documenti_contratto.length > 0 ? documenti_contratto : documenti;

  const stats = {
    contratti: contratti.filter(c => c.stato === 'attivo').length,
    documenti: documenti_personali.length + documenti_immobili.length + contractDocs.length,
  };

  // Group contract documents by contract
  const docsByContract = {};
  contractDocs.forEach(doc => {
    const key = doc.contract_code || doc.contract_id || 'altro';
    if (!docsByContract[key]) docsByContract[key] = [];
    docsByContract[key].push(doc);
  });

  // Determine user role per contract
  const getUserRoleInContract = (contract) => {
    if (contract.inquilini_ids?.includes(user?.id)) return 'inquilino';
    // Check via property
    const prop = immobili.find(i => i.id === contract.property_id);
    if (prop?.proprietari_ids?.includes(user?.id)) return 'proprietario';
    return null;
  };

  // Get payments for a contract filtered by user role
  const getContractPayments = (contract) => {
    const role = getUserRoleInContract(contract);
    // Escludi i movimenti straordinari dalla griglia rate (conguagli, imposte, CSV,
    // o creati da PagamentiStraordinari): sono mostrati nella sezione dedicata.
    // La prima_rata (bonifico iniziale) resta invece nella griglia come rata 1.
    const isStraordinarioMovimento = (p) => (
      p.tipo === 'straordinario' ||
      p.straordinario_id ||
      (p.categoria === 'straordinario' && p.straordinario_tipo !== 'prima_rata')
    );
    const contractPayments = pagamenti.filter(p => p.contract_id === contract.id && !isStraordinarioMovimento(p));
    if (role === 'inquilino') return contractPayments.filter(p => p.tipo === 'sdd');
    if (role === 'proprietario') return contractPayments.filter(p => p.tipo === 'sepa');
    return contractPayments;
  };

  return (
    <div className="user-layout" data-testid="user-dashboard">
      {/* Ghost Mode Banner */}
      {isGhostMode && (
        <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between" data-testid="ghost-mode-banner">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-medium">
              Modalità Ghost — Stai visualizzando come: {user?.nome} {user?.cognome}
              {is_proprietario && <Badge className="ml-2 bg-white/20 text-white text-xs">Locatore</Badge>}
              {is_inquilino && <Badge className="ml-2 bg-white/20 text-white text-xs">Conduttore</Badge>}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleBackToAdmin}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="ghost-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Torna al pannello admin
          </Button>
        </div>
      )}

      {/* Header */}
      <header className="user-header">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="font-semibold text-slate-900">SEMPLIFICAFFITTO</h1>
            <p className="text-sm text-slate-500">Area Riservata</p>
          </div>
        </div>
        {!isGhostMode && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <User className="w-4 h-4" />
              {user?.nome} {user?.cognome}
            </div>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="user-logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </div>
        )}
      </header>

      {/* Content */}
      <main className="user-content">
        {/* Welcome banner (visibile fino a primo dismiss) */}
        {showWelcome && (
          <WelcomeBanner
            userName={`${user?.nome || ''} ${user?.cognome || ''}`.trim()}
            onDismiss={dismissWelcome}
          />
        )}

        {/* Welcome + Anagrafica */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900">Ciao, {user?.nome}!</h2>
          <p className="text-slate-500 mt-1 mb-6">
            Benvenuto nella tua area riservata. Qui puoi consultare i tuoi contratti, documenti e pagamenti.
          </p>
          
          <AnagraficaCard user={user} />
        </div>

        {/* Stats */}
        <div className="bento-grid mb-8">
          <Card className="bento-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('contratti')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Contratti Attivi</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-contratti">{stats.contratti}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bento-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('documenti')}>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FolderOpen className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Documenti</p>
                  <p className="text-2xl font-bold text-slate-900" data-testid="stat-documenti">{stats.documenti}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="contratti" data-testid="tab-user-contratti">
              <FileText className="w-4 h-4 mr-2" />
              Contratti
            </TabsTrigger>
            <TabsTrigger value="immobili" data-testid="tab-user-immobili">
              <Building className="w-4 h-4 mr-2" />
              Immobili
            </TabsTrigger>
            <TabsTrigger value="documenti" data-testid="tab-user-documenti">
              <FolderOpen className="w-4 h-4 mr-2" />
              Documenti
            </TabsTrigger>
            <TabsTrigger value="privacy" data-testid="tab-user-privacy">
              <Shield className="w-4 h-4 mr-2" />
              Privacy & Dati
            </TabsTrigger>
          </TabsList>

          {/* ====== CONTRATTI TAB ====== */}
          <TabsContent value="contratti">
            {contratti.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">Nessun contratto trovato</CardContent></Card>
            ) : (
              <Accordion type="single" collapsible className="space-y-3">
                {contratti.map((cont) => {
                  const badge = getStatoBadge(cont.stato);
                  const Icon = badge.icon;
                  const prop = cont.property || {};
                  const role = getUserRoleInContract(cont);
                  const contPayments = getContractPayments(cont);

                  return (
                    <AccordionItem key={cont.id} value={cont.id} className="border rounded-lg bg-white shadow-sm" data-testid={`user-contratto-${cont.id}`}>
                      <AccordionTrigger className="px-6 py-4 hover:no-underline">
                        <div className="flex items-center gap-4 flex-1 text-left">
                          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                            <Home className="w-5 h-5 text-slate-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-sm font-semibold text-slate-700">{cont.codice_contratto}</span>
                              <Badge className={badge.cls}><Icon className="w-3 h-3 mr-1" />{badge.label}</Badge>
                              {role && (
                                <Badge variant="outline" className={role === 'proprietario' ? 'border-green-300 text-green-700' : 'border-blue-300 text-blue-700'}>
                                  {role === 'proprietario' ? 'Locatore' : 'Conduttore'}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-600 mt-1">{prop.indirizzo} — {prop.cap} {prop.citta} ({prop.provincia})</p>
                          </div>
                          <div className="text-right shrink-0 mr-4">
                            <p className="text-lg font-bold text-slate-900">{fmt(cont.canone_mensile)}</p>
                            <p className="text-xs text-slate-500">/mese</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      {/* Striscia Registrazione Contratto */}
                      {cont.registrazione_contratto && (
                        <div className="mx-6 mb-1 px-4 py-2 bg-blue-50 rounded-lg border border-blue-100" data-testid={`user-reg-strip-${cont.id}`}>
                          <div className="flex items-center gap-6 text-xs text-blue-700 flex-wrap">
                            <span className="font-medium text-blue-800 flex items-center gap-1">
                              <FileCheck className="w-3 h-3" />
                              Registrazione:
                            </span>
                            {cont.registrazione_contratto.ufficio_territoriale && (
                              <span>Ufficio: <strong>{cont.registrazione_contratto.ufficio_territoriale}</strong></span>
                            )}
                            {cont.registrazione_contratto.data_registrazione && (
                              <span>Data: <strong>{new Date(cont.registrazione_contratto.data_registrazione).toLocaleDateString('it-IT')}</strong></span>
                            )}
                            {cont.registrazione_contratto.protocollo && (
                              <span>Protocollo: <strong>{cont.registrazione_contratto.protocollo}</strong></span>
                            )}
                            {cont.registrazione_contratto.codice_identificativo && (
                              <span>Cod. Ident.: <strong>{cont.registrazione_contratto.codice_identificativo}</strong></span>
                            )}
                          </div>
                        </div>
                      )}
                      <AccordionContent className="px-6 pb-6">
                        {/* Riepilogo Contratto */}
                        <div className="bg-slate-50 rounded-lg p-4 mb-4">
                          <h4 className="font-semibold text-slate-700 text-sm mb-3">Riepilogo Contratto</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-slate-500">Tipo:</span>
                              <p className="font-medium">{cont.tipo_contratto || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Regime:</span>
                              <p className="font-medium capitalize">{cont.regime_fiscale || '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Inizio:</span>
                              <p className="font-medium">{cont.data_inizio ? new Date(cont.data_inizio).toLocaleDateString('it-IT') : '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Fine:</span>
                              <p className="font-medium">{cont.data_fine ? new Date(cont.data_fine).toLocaleDateString('it-IT') : '-'}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Canone mensile:</span>
                              <p className="font-medium">{fmt(cont.canone_mensile)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Spese condominiali:</span>
                              <p className="font-medium">{fmt(cont.spese_condominiali)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Bollo:</span>
                              <p className="font-medium">{fmt(cont.quota_bollo)}</p>
                            </div>
                            <div>
                              <span className="text-slate-500">Durata:</span>
                              <p className="font-medium">{cont.durata_mesi || '-'} mensilità</p>
                            </div>
                          </div>
                        </div>

                        {/* Storico Pagamenti per Annualità */}
                        <ContractPaymentsAnnualita
                          contPayments={contPayments}
                          contract={cont}
                          role={role}
                          fmt={fmt}
                          monthLabel={monthLabel}
                          getStatoBadge={getStatoBadge}
                          handleDownloadFatturaManuale={handleDownloadFatturaManuale}
                        />

                        {/* Movimenti Straordinari */}
                        <ContractStraordinari
                          pagamenti={pagamenti}
                          contract={cont}
                          fmt={fmt}
                          handleDownloadFatturaManuale={handleDownloadFatturaManuale}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </TabsContent>

          {/* ====== IMMOBILI TAB ====== */}
          <TabsContent value="immobili">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">I miei Immobili</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Indirizzo</TableHead>
                      <TableHead>Dati Catastali</TableHead>
                      <TableHead>Ruolo</TableHead>
                      <TableHead>Stato</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {immobili.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-500">Nessun immobile trovato</TableCell></TableRow>
                    ) : (
                      immobili.map((imm) => {
                        const isProp = imm.proprietari_ids?.includes(user?.id);
                        return (
                          <TableRow key={imm.id} data-testid={`user-immobile-${imm.id}`}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                                  <Building className="w-5 h-5 text-slate-600" />
                                </div>
                                <div>
                                  <p className="font-medium">{imm.indirizzo}</p>
                                  <p className="text-sm text-slate-500">{imm.cap} {imm.citta} ({imm.provincia})</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {imm.foglio || imm.particella || imm.subalterno ? (
                                  <>
                                    <span className="text-slate-500">Fg:</span> {imm.foglio || '-'} |{' '}
                                    <span className="text-slate-500">Part.:</span> {imm.particella || '-'} |{' '}
                                    <span className="text-slate-500">Sub.:</span> {imm.subalterno || '-'}
                                  </>
                                ) : (
                                  <span className="text-slate-400">Non disponibile</span>
                                )}
                              </div>
                              {imm.categoria && <p className="text-xs text-slate-400 mt-1">Cat. {imm.categoria}</p>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={isProp ? 'border-green-300 text-green-700' : 'border-blue-300 text-blue-700'}>
                                {isProp ? 'Proprietario' : 'Inquilino'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={imm.stato === 'in_gestione' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                                {imm.stato === 'in_gestione' ? 'In Gestione' : imm.stato === 'disponibile' ? 'Disponibile' : imm.stato}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ====== DOCUMENTI TAB ====== */}
          <TabsContent value="documenti">
            <div className="space-y-6">
              {documenti_accesso_scaduto && (
                <Card className="border-amber-300 bg-amber-50" data-testid="documenti-accesso-scaduto-banner">
                  <CardContent className="py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm text-amber-900">
                        <p className="font-medium">Alcuni documenti non sono più disponibili online</p>
                        <p className="text-amber-800">
                          Il periodo di consultazione ({grace_months} mesi dalla fine del contratto) è terminato.
                          Puoi richiederne una copia all'amministratore.
                        </p>
                      </div>
                      <Button
                        onClick={handleRequestDocuments}
                        disabled={requestingDocs}
                        className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
                        data-testid="richiedi-documenti-btn"
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        {requestingDocs ? 'Invio...' : 'Richiedi documenti via email'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Documenti Personali */}
              <Card>
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2">
                    <UserCircle className="w-5 h-5 text-slate-600" />
                    Documenti Personali
                    {documenti_personali.length > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">{documenti_personali.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {documenti_personali.length === 0 ? (
                    <p className="py-4 text-center text-slate-400 text-sm">Nessun documento personale caricato</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>File</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Data Caricamento</TableHead>
                          <TableHead className="text-right">Scarica</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {documenti_personali.map((doc) => (
                          <TableRow key={doc.id} data-testid={`user-doc-personale-${doc.id}`}>
                            <TableCell>
                              <p className="font-medium text-sm">{doc.filename}</p>
                              {doc.note && <p className="text-xs text-slate-500">{doc.note}</p>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="capitalize text-xs">{(doc.tipo_documento || doc.categoria || '').replace(/_/g, ' ')}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('it-IT') : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadPersonalDoc(doc)} data-testid={`download-personal-doc-${doc.id}`}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Documenti Immobili — solo per proprietari */}
              <Card>
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Landmark className="w-5 h-5 text-slate-600" />
                    Documenti Immobili
                    {documenti_immobili.length > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">{documenti_immobili.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {documenti_immobili.length === 0 ? (
                    <p className="py-4 text-center text-slate-400 text-sm">
                      Nessun documento immobile disponibile
                    </p>
                  ) : (() => {
                    // Raggruppa per property_id
                    const byProperty = {};
                    documenti_immobili.forEach(doc => {
                      const key = doc.property_id;
                      if (!byProperty[key]) byProperty[key] = { label: doc.property_indirizzo, docs: [] };
                      byProperty[key].docs.push(doc);
                    });
                    return (
                      <div className="space-y-3 py-2">
                        {Object.entries(byProperty).map(([propId, group]) => (
                          <div key={propId} className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <Building className="w-4 h-4 text-slate-500" />
                              <span className="font-medium text-sm">{group.label}</span>
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>File</TableHead>
                                  <TableHead>Tipo</TableHead>
                                  <TableHead>Data</TableHead>
                                  <TableHead className="text-right">Scarica</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {group.docs.map((doc) => (
                                  <TableRow key={doc.id} data-testid={`user-doc-immobile-${doc.id}`}>
                                    <TableCell>
                                      <p className="font-medium text-sm">{doc.filename}</p>
                                      {doc.note && <p className="text-xs text-slate-500">{doc.note}</p>}
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant="secondary" className="capitalize text-xs">{(doc.tipo_documento || '').replace(/_/g, ' ')}</Badge>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('it-IT') : '-'}
                                    </TableCell>
                                    <TableCell className="text-right">
                                      <Button variant="ghost" size="sm" onClick={() => handleDownloadPropertyDoc(doc)} data-testid={`download-property-doc-${doc.id}`}>
                                        <Download className="w-4 h-4" />
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Documenti Contratti */}
              <Card>
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-slate-600" />
                    Documenti Contratto
                    {contractDocs.length > 0 && (
                      <Badge variant="secondary" className="ml-2 text-xs">{contractDocs.length}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {contratti.length === 0 ? (
                    <p className="py-4 text-center text-slate-400 text-sm">Nessun contratto associato</p>
                  ) : (
                    <div className="space-y-3 py-2">
                      {contratti.map((cont) => {
                        const contDocs = docsByContract[cont.codice_contratto] || docsByContract[cont.id] || [];
                        return (
                          <div key={cont.id} className="border rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-slate-500" />
                              <span className="font-medium text-sm">Contratto {cont.codice_contratto}</span>
                              <span className="text-xs text-slate-400">— {cont.property?.indirizzo}</span>
                            </div>
                            {contDocs.length === 0 ? (
                              <p className="text-sm text-slate-400 text-center py-2">Nessun documento per questo contratto</p>
                            ) : (
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>File</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead className="text-right">Scarica</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {contDocs.map((doc) => (
                                    <TableRow key={doc.id} data-testid={`user-documento-${doc.id}`}>
                                      <TableCell>
                                        <p className="font-medium text-sm">{doc.filename}</p>
                                        {doc.note && <p className="text-xs text-slate-500">{doc.note}</p>}
                                      </TableCell>
                                      <TableCell>
                                        <Badge variant="secondary" className="capitalize text-xs">{(doc.tipo_documento || '').replace(/_/g, ' ')}</Badge>
                                      </TableCell>
                                      <TableCell className="text-sm">{doc.caricato_il ? new Date(doc.caricato_il).toLocaleDateString('it-IT') : '-'}</TableCell>
                                      <TableCell className="text-right">
                                        <Button variant="ghost" size="sm" onClick={() => handleDownloadDoc(doc)} data-testid={`download-user-doc-${doc.id}`}>
                                          <Download className="w-4 h-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ====== PRIVACY TAB ====== */}
          <TabsContent value="privacy" data-testid="tab-content-privacy">
            <GdprSection userEmail={user?.email} />
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
};

// === Sub-components ===

const AnagraficaCard = ({ user }) => {
  const isSoc = user?.is_societa;
  // Per le società: nome/cognome del record = Legale Rappresentante,
  // e la sua residenza è nei campi rappresentante_*.
  const resInd = isSoc ? user?.rappresentante_indirizzo : (user?.indirizzo_residenza || user?.indirizzo);
  const resCap = isSoc ? user?.rappresentante_cap : user?.cap_residenza;
  const resCitta = isSoc ? user?.rappresentante_citta : user?.citta_residenza;
  const resProv = isSoc ? user?.rappresentante_provincia : user?.provincia_residenza;
  // Sede legale composta dai campi separati (fallback al campo legacy singolo)
  const sedeCitta = [user?.sede_legale_cap, user?.sede_legale_citta].filter(Boolean).join(' ');
  const sedeProv = user?.sede_legale_provincia ? `(${user.sede_legale_provincia})` : '';
  const sedeLegale = [user?.sede_legale_indirizzo, `${sedeCitta} ${sedeProv}`.trim()]
    .filter(Boolean).join(' - ') || user?.sede_legale || '-';
  // Rapp. legale = nome+cognome (per le società coincide con il legale rappresentante)
  const rappLegale = (user?.rappresentante_legale || `${user?.nome || ''} ${user?.cognome || ''}`.trim()) || '-';

  return (
  <Card className="bg-slate-50 border-slate-200">
    <CardContent className="p-4 sm:p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="space-y-3">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Dati Personali</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><span className="text-sm">{user?.nome} {user?.cognome}</span></div>
            <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span className="text-sm">{user?.email || '-'}</span></div>
            <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><span className="text-sm">{user?.telefono || '-'}</span></div>
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Dati Fiscali</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><IdCard className="w-4 h-4 text-slate-400" /><span className="text-sm font-mono">{user?.codice_fiscale || '-'}</span></div>
            {user?.partita_iva && <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /><span className="text-sm font-mono">{user?.partita_iva}</span></div>}
            {user?.sdi && <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-slate-400" /><span className="text-sm">SDI: {user?.sdi}</span></div>}
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">Dati Bancari</h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2"><Iban className="w-4 h-4 text-slate-400" /><span className="text-sm font-mono break-all">{user?.iban || '-'}</span></div>
            {user?.pec && <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><span className="text-sm">{user?.pec}</span></div>}
          </div>
        </div>
        <div className="space-y-3">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide">{isSoc ? 'Residenza Rapp.' : 'Residenza'}</h3>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
            <div className="text-sm">
              <p>{resInd || '-'}</p>
              {(resCap || resCitta) && (
                <p className="text-slate-500">{resCap} {resCitta} {resProv ? `(${resProv})` : ''}</p>
              )}
            </div>
          </div>
        </div>
      </div>
      {isSoc && (
        <div className="mt-6 pt-4 border-t border-slate-200">
          <h3 className="font-semibold text-slate-700 text-sm uppercase tracking-wide mb-3">Dati Societari</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /><span className="text-sm"><span className="text-slate-500">Ragione Sociale:</span> {user?.ragione_sociale || '-'}</span></div>
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-slate-400 mt-0.5" /><span className="text-sm"><span className="text-slate-500">Sede Legale:</span> {sedeLegale}</span></div>
            <div className="flex items-center gap-2"><User className="w-4 h-4 text-slate-400" /><span className="text-sm"><span className="text-slate-500">Rapp. Legale:</span> {rappLegale}</span></div>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
  );
};

const ContractPaymentsAnnualita = ({ contPayments, contract, role, fmt, monthLabel, getStatoBadge, handleDownloadFatturaManuale }) => {
  const [expandedAnns, setExpandedAnns] = React.useState({});

  // Calcola annualità basata su data_inizio del contratto
  const dataInizio = contract.data_inizio ? new Date(contract.data_inizio) : null;
  const startMonth = dataInizio ? dataInizio.getFullYear() * 12 + dataInizio.getMonth() : 0;

  // Calcola totale rate dal tipo contratto
  const tipoContratto = contract.tipo_contratto || '4+4';
  let totaleRate = contract.durata_mesi || 48;
  try {
    const parts = tipoContratto.split('+');
    totaleRate = (parseInt(parts[0]) + (parts[1] ? parseInt(parts[1]) : 0)) * 12;
  } catch (e) {
    console.warn('Parsing tipo_contratto fallito, uso fallback', tipoContratto, e);
  }
  if (dataInizio && dataInizio.getDate() > 1) totaleRate += 1;

  // Raggruppa per annualità
  const annualitaMap = {};
  const sortedPayments = [...contPayments].sort((a, b) => a.month.localeCompare(b.month));

  sortedPayments.forEach((pag) => {
    const pagDate = new Date(pag.month);
    const pagMonth = pagDate.getFullYear() * 12 + pagDate.getMonth();
    const monthIndex = pagMonth - startMonth;
    const annNum = Math.floor(monthIndex / 12) + 1;
    const rataNumero = pag.rata_numero || (monthIndex + 1);

    if (!annualitaMap[annNum]) annualitaMap[annNum] = [];
    annualitaMap[annNum].push({ ...pag, _rataNumero: rataNumero, _totaleRate: pag.rata_totale || totaleRate });
  });

  // Ordina annualità: più recente in alto
  const annualitaKeys = Object.keys(annualitaMap).map(Number).sort((a, b) => b - a);

  // Auto-espandi ultima annualità (più recente)
  React.useEffect(() => {
    if (annualitaKeys.length > 0) {
      setExpandedAnns({ [annualitaKeys[0]]: true });
    }
  }, [contract.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (contPayments.length === 0) {
    return <p className="text-sm text-slate-500 py-4 text-center">Nessun pagamento per questo contratto</p>;
  }

  const handleExportPdf = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/contracts/${contract.id}/export-storico-utente/pdf?role=${role}`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `storico_${contract.codice_contratto}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Errore generazione PDF');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-slate-700 text-sm">
          {role === 'inquilino' ? 'Storico Addebiti SDD' : role === 'proprietario' ? 'Storico Accrediti Bonifico' : 'Storico Pagamenti'}
          <span className="text-slate-400 font-normal ml-2">({contPayments.length} mensilità)</span>
        </h4>
        <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid={`export-pdf-${contract.id}`}>
          <FileDown className="w-4 h-4 mr-1" />
          Scarica PDF
        </Button>
      </div>

      {annualitaKeys.map(annNum => {
        const payments = annualitaMap[annNum];
        const isExpanded = expandedAnns[annNum] || false;
        // Ordina: più recente in alto
        const sortedAnn = [...payments].sort((a, b) => b.month.localeCompare(a.month));

        return (
          <div key={annNum} className="border rounded-lg overflow-hidden" data-testid={`user-annualita-${annNum}`}>
            <button
              className="w-full bg-gradient-to-r from-slate-600 to-slate-500 text-white px-4 py-2 flex items-center justify-between hover:from-slate-500 hover:to-slate-400 transition-colors text-sm"
              onClick={() => setExpandedAnns(prev => ({ ...prev, [annNum]: !prev[annNum] }))}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-bold">{annNum}° Annualità</span>
                <span className="text-slate-300 text-xs">({payments.length} mensilità)</span>
              </div>
            </button>

            {isExpanded && (
              <>
                {/* Desktop: tabella */}
                <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 text-xs">
                        <TableHead className="py-1.5 w-16 font-semibold">Mens.</TableHead>
                        <TableHead className="py-1.5 font-semibold">Mese</TableHead>
                        <TableHead className="py-1.5 font-semibold">Dettaglio</TableHead>
                        <TableHead className="text-right py-1.5 font-semibold">Importo</TableHead>
                        <TableHead className="py-1.5 text-center font-semibold">Stato</TableHead>
                        <TableHead className="py-1.5 text-center font-semibold">Doc.</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedAnn.map(pag => (
                        <UserPaymentRow key={pag.id} pag={pag} role={role} fmt={fmt} monthLabel={monthLabel} getStatoBadge={getStatoBadge} handleDownloadFatturaManuale={handleDownloadFatturaManuale} />
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Mobile: schede impilate */}
                <div className="md:hidden p-2 space-y-2 bg-slate-50">
                  {sortedAnn.map(pag => (
                    <UserPaymentCard key={pag.id} pag={pag} role={role} fmt={fmt} monthLabel={monthLabel} getStatoBadge={getStatoBadge} handleDownloadFatturaManuale={handleDownloadFatturaManuale} />
                  ))}
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

const UserPaymentRow = ({ pag, role, fmt, monthLabel, getStatoBadge, handleDownloadFatturaManuale }) => {
  const badge = getStatoBadge(pag.status);
  const Icon = badge.icon;
  const comp = pag.components || {};
  const rataNum = pag._rataNumero || pag.rata_numero || '-';
  const rataTot = pag._totaleRate || pag.rata_totale || '-';

  if (role === 'inquilino') {
    const canone = comp.affitto_mensile || comp.affitto || 0;
    const condominio = comp.quota_condominio || comp.condominio || 0;
    const bollo = comp.imposta_bollo || comp.bollo || 0;
    const sa9 = comp.sa_9_conduttore || comp.sa_conduttore || 0;
    const sconto = comp.sconto_sa_inq || comp.sconto || 0;
    return (
      <TableRow className="text-sm" data-testid={`user-pag-${pag.id}`}>
        <TableCell className="py-1.5 font-mono text-xs font-bold text-center">{rataNum}/{rataTot}</TableCell>
        <TableCell className="py-1.5 capitalize text-sm">{monthLabel(pag.month)}</TableCell>
        <TableCell className="py-1.5">
          <div className="text-[11px] space-y-0 text-slate-600">
            <span>Canone {fmt(canone)}</span>
            {condominio > 0 && <span> + Cond. {fmt(condominio)}</span>}
            {bollo > 0 && <span> + Bollo {fmt(bollo)}</span>}
            {sa9 > 0 && <span> + SA {fmt(sa9)}</span>}
            {sconto > 0 && <span className="text-red-500"> - Sconto {fmt(sconto)}</span>}
          </div>
        </TableCell>
        <TableCell className="text-right py-1.5 font-bold text-blue-700">{fmt(pag.sdd_amount)}</TableCell>
        <TableCell className="py-1.5 text-center"><Badge className={`text-[10px] ${badge.cls}`}><Icon className="w-3 h-3 mr-0.5" />{badge.label}</Badge></TableCell>
        <TableCell className="py-1.5 text-center">
          {pag.fattura_manuale ? (
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handleDownloadFatturaManuale(pag.id)}
              title={pag.fattura_numero ? `Scarica Fattura ${pag.fattura_numero}` : 'Scarica fattura'}
              data-testid={`download-fattura-inq-${pag.id}`}>
              <Download className="w-3 h-3 text-blue-600" />
            </Button>
          ) : pag.fattura_numero ? (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-blue-50 text-blue-500" 
              title={`Fattura ${pag.fattura_numero} — ${pag.fattura_data || ''} (PDF non caricato)`}
              data-testid={`fattura-numero-inq-${pag.id}`}>
              <FileText className="w-3 h-3" />
            </span>
          ) : <span className="text-slate-300 text-xs">-</span>}
        </TableCell>
      </TableRow>
    );
  }

  // Proprietario
  const canone = comp.affitto_mensile || comp.affitto || 0;
  const condominio = comp.quota_condominio || comp.condominio || 0;
  const sa7 = comp.sa_7_locatore || comp.sa_locatore || 0;
  return (
    <TableRow className="text-sm" data-testid={`user-pag-${pag.id}`}>
      <TableCell className="py-1.5 font-mono text-xs font-bold text-center">{rataNum}/{rataTot}</TableCell>
      <TableCell className="py-1.5 capitalize text-sm">{monthLabel(pag.month)}</TableCell>
      <TableCell className="py-1.5">
        <div className="text-[11px] space-y-0 text-slate-600">
          <span>Canone {fmt(canone)}</span>
          {condominio > 0 && <span> + Cond. {fmt(condominio)}</span>}
          {sa7 > 0 && <span className="text-red-500"> - SA {fmt(sa7)}</span>}
        </div>
      </TableCell>
      <TableCell className="text-right py-1.5 font-bold text-green-700">{fmt(pag.sepa_amount)}</TableCell>
      <TableCell className="py-1.5 text-center"><Badge className={`text-[10px] ${badge.cls}`}><Icon className="w-3 h-3 mr-0.5" />{badge.label}</Badge></TableCell>
      <TableCell className="py-1.5 text-center">
        {pag.fattura_manuale ? (
          <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => handleDownloadFatturaManuale(pag.id)}
            title={pag.fattura_numero ? `Scarica Fattura ${pag.fattura_numero}` : 'Scarica fattura'}
            data-testid={`download-fattura-loc-${pag.id}`}>
            <Download className="w-3 h-3 text-green-600" />
          </Button>
        ) : pag.fattura_numero ? (
          <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-green-50 text-green-500" 
            title={`Fattura ${pag.fattura_numero} — ${pag.fattura_data || ''} (PDF non caricato)`}
            data-testid={`fattura-numero-loc-${pag.id}`}>
            <FileText className="w-3 h-3" />
          </span>
        ) : <span className="text-slate-300 text-xs">-</span>}
      </TableCell>
    </TableRow>
  );
};

const UserPaymentCard = ({ pag, role, fmt, monthLabel, getStatoBadge, handleDownloadFatturaManuale }) => {
  const badge = getStatoBadge(pag.status);
  const Icon = badge.icon;
  const comp = pag.components || {};
  const rataNum = pag._rataNumero || pag.rata_numero || '-';
  const rataTot = pag._totaleRate || pag.rata_totale || '-';
  const isInq = role === 'inquilino';
  const importo = isInq ? pag.sdd_amount : pag.sepa_amount;
  const importoCls = isInq ? 'text-blue-700' : 'text-green-700';
  const canone = comp.affitto_mensile || comp.affitto || 0;
  const condominio = comp.quota_condominio || comp.condominio || 0;
  const bollo = comp.imposta_bollo || comp.bollo || 0;
  const sa9 = comp.sa_9_conduttore || comp.sa_conduttore || 0;
  const sconto = comp.sconto_sa_inq || comp.sconto || 0;
  const sa7 = comp.sa_7_locatore || comp.sa_locatore || 0;
  return (
    <div className="border rounded-lg p-3 bg-white shadow-sm" data-testid={`user-pag-card-${pag.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-[11px] font-bold text-slate-500 shrink-0">{rataNum}/{rataTot}</span>
          <span className="capitalize font-semibold text-sm truncate">{monthLabel(pag.month)}</span>
        </div>
        <span className={`font-bold text-base ${importoCls} shrink-0`}>{fmt(importo)}</span>
      </div>
      <div className="mt-1.5 text-[11px] text-slate-600 leading-relaxed">
        <span>Canone {fmt(canone)}</span>
        {condominio > 0 && <span> + Cond. {fmt(condominio)}</span>}
        {isInq && bollo > 0 && <span> + Bollo {fmt(bollo)}</span>}
        {isInq && sa9 > 0 && <span> + SA {fmt(sa9)}</span>}
        {isInq && sconto > 0 && <span className="text-red-500"> − Sconto {fmt(sconto)}</span>}
        {!isInq && sa7 > 0 && <span className="text-red-500"> − SA {fmt(sa7)}</span>}
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between gap-2">
        <Badge className={`text-[10px] ${badge.cls}`}><Icon className="w-3 h-3 mr-0.5" />{badge.label}</Badge>
        {pag.fattura_manuale ? (
          <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => handleDownloadFatturaManuale(pag.id)}
            title={pag.fattura_numero ? `Scarica Fattura ${pag.fattura_numero}` : 'Scarica fattura'}
            data-testid={`download-fattura-card-${pag.id}`}>
            <Download className="w-3 h-3 mr-1" /> Fattura
          </Button>
        ) : pag.fattura_numero ? (
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500"
            title={`Fattura ${pag.fattura_numero} — ${pag.fattura_data || ''} (PDF non caricato)`}
            data-testid={`fattura-numero-card-${pag.id}`}>
            <FileText className="w-3 h-3" /> {pag.fattura_numero}
          </span>
        ) : <span className="text-slate-300 text-[11px]">nessuna fattura</span>}
      </div>
    </div>
  );
};

// Componente Movimenti Straordinari per il portale utente
const ContractStraordinari = ({ pagamenti, contract, fmt, handleDownloadFatturaManuale }) => {
  const allStraordinari = pagamenti.filter(p => 
    p.contract_id === contract.id && (p.tipo === 'straordinario' || p.categoria === 'straordinario')
  );
  
  // Deduplica: per quelli con straordinario_id (SDD+SEPA), mostra solo una volta
  const seen = new Set();
  const straordinari = allStraordinari.filter(p => {
    if (p.straordinario_id) {
      if (seen.has(p.straordinario_id)) return false;
      seen.add(p.straordinario_id);
    }
    return true;
  });
  
  const [expanded, setExpanded] = useState(false);

  if (straordinari.length === 0) return null;

  const TIPO_LABELS = {
    prima_rata: 'Prima Rata (Bonifico)',
    imposta_registro: 'Imposta di Registro',
    imposta_registro_annualita: 'Imp. Registro Annualità',
    conguaglio_condominio: 'Conguaglio Condominio',
    intervento_riparazione: 'Intervento/Riparazione',
    cauzione: 'Cauzione',
    penale: 'Penale',
    rimborso: 'Rimborso',
    altro: 'Altro',
  };

  const totale = straordinari.reduce((s, m) => s + (m.importo || m.sdd_amount || 0), 0);

  return (
    <div className="mt-4 border rounded-lg overflow-hidden" data-testid="user-straordinari-section">
      <button
        className="w-full bg-gradient-to-r from-amber-700 to-amber-600 text-white px-4 py-2.5 flex items-center justify-between hover:from-amber-600 hover:to-amber-500 transition-colors"
        onClick={() => setExpanded(!expanded)}
        data-testid="user-straordinari-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <h3 className="font-bold text-sm">Movimenti Straordinari</h3>
          <span className="text-xs text-amber-200 ml-1">({straordinari.length})</span>
        </div>
        <span className="text-xs">Totale: <strong>{fmt(totale)}</strong></span>
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="text-xs bg-amber-50">
                <TableHead className="py-2 font-semibold">Data</TableHead>
                <TableHead className="py-2 font-semibold">Tipo</TableHead>
                <TableHead className="py-2 font-semibold">N° Fattura</TableHead>
                <TableHead className="text-right py-2 font-semibold">Importo</TableHead>
                <TableHead className="py-2 text-center font-semibold">Doc.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {straordinari.sort((a, b) => (a.data || a.month || '').localeCompare(b.data || b.month || '')).map((mov, idx) => (
                <TableRow key={mov.id || idx} className="text-sm hover:bg-amber-50/50" data-testid={`user-straordinario-${idx}`}>
                  <TableCell className="py-1.5 font-medium whitespace-nowrap">
                    {mov.fattura_data || (mov.data ? new Date(mov.data).toLocaleDateString('it-IT') : '-')}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs">
                    {TIPO_LABELS[mov.straordinario_tipo] || mov.straordinario_descrizione || '-'}
                  </TableCell>
                  <TableCell className="py-1.5 font-mono text-xs">{mov.fattura_numero || '-'}</TableCell>
                  <TableCell className="text-right py-1.5 font-semibold tabular-nums text-amber-700">
                    {fmt(mov.importo || mov.sdd_amount || 0)}
                  </TableCell>
                  <TableCell className="py-1.5 text-center">
                    {mov.fattura_manuale ? (
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0"
                        onClick={() => handleDownloadFatturaManuale(mov.id)}
                        data-testid={`user-download-straordinario-${idx}`}>
                        <Download className="w-3 h-3 text-amber-600" />
                      </Button>
                    ) : <span className="text-slate-300 text-xs">-</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* end */}
    </div>
  );
};

export default UserDashboard;
